import Tesseract from 'tesseract.js';
import moment from 'moment';
import * as pdfjsLib from 'pdfjs-dist';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Helper to convert Blob/File to base64
const toBase64 = (fileOrBlob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(fileOrBlob);
    });
};

/**
 * Scans an image/PDF (first page) for invoice data.
 * @param {string|File} source - URL or File object.
 * @returns {Promise<Object>} - Extracted data { total, date, provider, invoiceId }
 */
export const scanInvoice = async (source) => {
    try {
        console.log("Starting OCR scan...", source);
        let imageToScan = source;

        // 1. Detect if it is a PDF
        // Check File object type OR string URL extension
        let isPdf = false;
        if (source instanceof File) {
            isPdf = source.type === 'application/pdf';
        } else if (typeof source === 'string') {
            const lower = source.toLowerCase();
            isPdf = lower.includes('.pdf') || lower.includes('application/pdf');
        }

        if (isPdf) {
            console.log("PDF detected. Converting to image...");
            imageToScan = await convertPdfToImage(source);
        }

        // 2. Call backend Cloud Function for Gemini OCR
        try {
            console.log("Calling backend Cloud Function for Gemini OCR...");
            let blobData = imageToScan;
            
            if (typeof blobData === 'string') {
                const response = await fetch(blobData);
                blobData = await response.blob();
            }

            const base64Image = await toBase64(blobData);
            const mimeType = blobData.type || 'image/png';

            const analizarFactura = httpsCallable(functions, 'analizarFacturaConIA');
            const res = await analizarFactura({ base64Image, mimeType });
            const resultObj = res.data;

            console.log("Backend Gemini OCR response:", resultObj);

            // Re-map to local properties expected by the module
            return {
                monto: resultObj.monto ? parseFloat(resultObj.monto) : null,
                subtotal: resultObj.subtotal ? parseFloat(resultObj.subtotal) : null,
                iva: resultObj.iva ? parseFloat(resultObj.iva) : null,
                fecha: resultObj.fecha ? moment(resultObj.fecha, 'YYYY-MM-DD').toDate() : null,
                proveedor: resultObj.proveedor || null,
                descripcion: resultObj.descripcion || 'Gasto escaneado con IA'
            };
        } catch (functionErr) {
            console.error("Backend Gemini OCR failed, falling back to local Tesseract.js:", functionErr);
            
            // 3. Fallback to local Tesseract OCR engine
            const { data: { text } } = await Tesseract.recognize(
                imageToScan,
                'eng+spa',
                { logger: m => console.log(m) }
            );

            console.log("Tesseract OCR Result Text:", text);
            return parseInvoiceText(text);
        }

    } catch (error) {
        console.error("OCR/AI Scan Error:", error);
        throw new Error("No se pudo escanear el documento. " + error.message);
    }
};

/**
 * Converts the first page of a PDF to a Blob URL (Image).
 */
const convertPdfToImage = async (pdfSource) => {
    try {
        let loadingTask;
        if (pdfSource instanceof File) {
            // Read file as ArrayBuffer for local files
            const arrayBuffer = await pdfSource.arrayBuffer();
            loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        } else {
            // URL
            loadingTask = pdfjsLib.getDocument(pdfSource);
        }

        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1); // Get first page

        const scale = 2.0; // Higher scale for better OCR
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
            canvasContext: context,
            viewport: viewport
        };

        await page.render(renderContext).promise;

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob); // Pass Blob directly to Tesseract
            }, 'image/png');
        });

    } catch (error) {
        console.error("PDF Conversion Error:", error);
        throw new Error("Error al convertir PDF a imagen: " + error.message);
    }
};

/**
 * Heuristic parser for invoice text.
 */
const parseInvoiceText = (text) => {
    const lines = text.split('\n');
    let extracted = {
        monto: null,
        subtotal: null,
        iva: null,
        fecha: null,
        descripcion: 'Gasto escaneado', // Default
        proveedor: null
    };

    // --- 0. Detect Provider First (for context-aware parsing) ---
    const lowerText = text.toLowerCase();
    if (lowerText.includes('cfe') || lowerText.includes('comision federal')) extracted.proveedor = 'CFE';
    else if (lowerText.includes('telmex')) extracted.proveedor = 'Telmex';
    else if (lowerText.includes('siapa') || lowerText.includes('agua')) extracted.proveedor = 'SIAPA';
    else if (lowerText.includes('izzi')) extracted.proveedor = 'Izzi';

    // Fallback: If provider is not matching typical ones, extract from first valid line
    if (!extracted.proveedor) {
        const lineCandidates = lines
            .map(l => l.trim())
            .filter(l => l.length > 3 && 
                         !l.toLowerCase().includes('factura') && 
                         !l.toLowerCase().includes('folio') && 
                         !l.toLowerCase().includes('fecha') && 
                         !l.toLowerCase().includes('rfc') && 
                         !l.toLowerCase().includes('emisor') &&
                         !l.toLowerCase().includes('receptor') &&
                         !/^\d+$/.test(l));
        if (lineCandidates.length > 0) {
            extracted.proveedor = lineCandidates[0].substring(0, 40);
        }
    }

    // Clean text for amount search
    const cleanText = text.replace(/MXN|USD/gi, '');

    let extractedAmount = 0;

    // STRATEGY A1: Explicit "Total a Pagar" (Very high confidence)
    // Matches: "Total a Pagar $1,506.46"
    // eslint-disable-next-line no-useless-escape
    const explicitTotalRegex = /(?:total\s+a\s+pagar|importe\s+a\s+pagar|cantidad\s+a\s+pagar|gran\s+total)[\s\w\.:$]*?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i;
    const explicitMatch = cleanText.match(explicitTotalRegex);

    if (explicitMatch && explicitMatch[1]) {
        extractedAmount = parseFloat(explicitMatch[1].replace(/,/g, ''));
    }

    // STRATEGY A2: "Total" label in breakdown (Medium confidence)
    // Essential for CFE breakdown: "Total 1,506.46" vs "Fac. del Periodo 1,508.31"
    if (!extractedAmount) {
        // Look for "Total" followed closely by a number. ignore "Subtotal".
        // Use word boundary to avoid Subtotal.
        // eslint-disable-next-line no-useless-escape
        const strictTotalRegex = /\bTotal[\s\.:$]+(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i;
        const strictMatch = cleanText.match(strictTotalRegex);

        if (strictMatch && strictMatch[1]) {
            extractedAmount = parseFloat(strictMatch[1].replace(/,/g, ''));
        }
    }

    // STRATEGY B: Line-by-Line analysis with Exclusion Logic
    if (!extractedAmount) {
        let maxAmount = 0;
        let pMaxAmount = 0; // Priority Max (with $ sign)

        // We process line by line to apply exclusion filters based on keywords in the *same line*
        for (const line of lines) {
            const lowerLine = line.toLowerCase();

            // --- CFE SPECIFIC FILTER ---
            // Exclude "Fac. del Periodo", "Subtotal", "Anterior", "SuPago" lines as they often outrank total
            if (extracted.proveedor === 'CFE') {
                if (lowerLine.includes('periodo') || lowerLine.includes('fac') || lowerLine.includes('subtotal') || lowerLine.includes('anterior') || lowerLine.includes('supago')) {
                    continue;
                }
            }

            const lineClean = line.replace(/MXN|USD/gi, '');
            // Match number with optional prefix
            // eslint-disable-next-line no-useless-escape
            const lineMatches = lineClean.matchAll(/([\$\s]|^)(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/g);

            for (const match of lineMatches) {
                const prefix = match[1];
                const valStr = match[2];
                const val = parseFloat(valStr.replace(/,/g, ''));

                const isInteger = !valStr.includes('.');
                const hasDollarSign = prefix && prefix.includes('$');

                // 1. Filter huge IDs
                if (val > 50000 && isInteger && !hasDollarSign) continue;

                // 2. Filter typical ID/Year numbers without dollars
                if (!hasDollarSign) {
                    if (val > 5000 && isInteger) continue;
                    if ((val >= 2020 && val <= 2035) && isInteger) continue;
                }

                // 3. Track Max
                if (hasDollarSign) {
                    if (val > pMaxAmount && val < 500000) pMaxAmount = val;
                }
                if (val > maxAmount && val < 500000) maxAmount = val;
            }
        }

        // Prefer amount with $ sign matching CFE style
        if (pMaxAmount > 0) extractedAmount = pMaxAmount;
        else if (maxAmount > 0) extractedAmount = maxAmount;
    }

    if (extractedAmount > 0) extracted.monto = extractedAmount;

    // --- 1.5 Extract Subtotal & IVA ---
    const subtotalRegex = /(?:subtotal|sub-total|neto|sub\s*total)[\s\w\.:$]*?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i;
    const subtotalMatch = cleanText.match(subtotalRegex);
    if (subtotalMatch && subtotalMatch[1]) {
        extracted.subtotal = parseFloat(subtotalMatch[1].replace(/,/g, ''));
    }

    const ivaRegex = /(?:iva|impuestos\s+trasladados|impuesto)[\s\w\.:$%]*?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/i;
    const ivaMatch = cleanText.match(ivaRegex);
    if (ivaMatch && ivaMatch[1]) {
        extracted.iva = parseFloat(ivaMatch[1].replace(/,/g, ''));
    }

    // --- 2. Extract Date ---
    // Strict day regex: 01-31 (avoids '0' OCR errors)
    // Patterns: DD/MM/YYYY, 02 ENE 2024

    // Priority: Specific "Limite de Pago" / "Vencimiento" detection (User Preference)
    // eslint-disable-next-line no-useless-escape
    const limitRegex = /(?:limite|pago|vencimiento)[^0-9]{0,15}(\b([1-9]|[12]\d|3[01])[\/\-\.\s](?:[a-zA-Z]{3,}|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)[\/\-\.\s](?:20\d{2}|\d{2}))/i;
    const limitMatch = text.match(limitRegex);

    if (limitMatch) {
        // The second capturing group `limitMatch[1]` contains the full date string
        let dateStr = limitMatch[1];
        const formats = [
            'DD/MM/YYYY', 'DD-MM/YYYY', 'YYYY-MM-DD', 'DD.MM.YYYY',
            'DD MMM YYYY', 'DD/MMM/YYYY', 'DD MMMM YYYY',
            'DD/MM/YY', 'DD-MM-YY'
        ];
        const parsed = moment(dateStr, formats, 'es', true); // Use 'es' locale for Spanish month names
        if (parsed.isValid() && parsed.year() >= 2020 && parsed.year() <= 2030) {
            extracted.fecha = parsed.toDate();
        }
    }

    // General iterate through all potential date-like strings if no limit date was found
    // Regex enforces Day 1-31 (rejects '0')
    if (!extracted.fecha) {
        // eslint-disable-next-line no-useless-escape
        const dateMatches = text.matchAll(/\b([1-9]|[12]\d|3[01])[\/\-\.\s](?:[0-1]?\d|[a-zA-Z]{3,}|enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)[\/\-\.\s](?:20\d{2}|\d{2})/gi);

        for (const match of dateMatches) {
            let dateStr = match[0];
            // Try standard formats including Spanish months
            const formats = [
                'DD/MM/YYYY', 'DD-MM/YYYY', 'YYYY-MM-DD', 'DD.MM.YYYY',
                'DD MMM YYYY', 'DD/MMM/YYYY', 'DD MMMM YYYY',
                'DD MMM YY', 'D MMM YY', 'DD MMMM YY', 'DD/MMM/YY', // Added 2-digit year support
                'DD/MM/YY', 'DD-MM-YY'
            ];

            const parsed = moment(dateStr, formats, 'es', true); // Use 'es' locale for Spanish month names
            // Validate range (avoid picking birthdates or future crazies)
            if (parsed.isValid() && parsed.year() >= 2020 && parsed.year() <= 2030) {
                extracted.fecha = parsed.toDate();
                break; // Take first valid date found
            }
        }
    }

    // --- 3. Provider (Simple Keyword Matching) ---
    // If provider was not detected at step 0 (redundant fallback) or if we want to confirm
    if (!extracted.proveedor) {
        const lowerText = text.toLowerCase();
        if (lowerText.includes('cfe') || lowerText.includes('comision federal')) extracted.proveedor = 'CFE';
        else if (lowerText.includes('telmex')) extracted.proveedor = 'Telmex';
        else if (lowerText.includes('siapa') || lowerText.includes('agua')) extracted.proveedor = 'SIAPA';
        else if (lowerText.includes('izzi')) extracted.proveedor = 'Izzi';
    }

    // --- 4. Extract Billing Period (User Request) ---
    // CFE specific: "PERIODO FACTURADO: 23 SEP 25 - 21 NOV 25"
    // Generic: "Periodo: 01/01/2024 al 31/01/2024"

    // Regex for CFE/Range style: "23 SEP 25 - 21 NOV 25"
    const periodRegex = /(?:periodo|facturado|consumo)[^0-9]*(\d{1,2}\s+[a-zA-Z]{3,}\s+\d{2,4}\s*-\s*\d{1,2}\s+[a-zA-Z]{3,}\s+\d{2,4})/i;
    const periodMatch = text.match(periodRegex);

    if (periodMatch && periodMatch[1]) {
        extracted.periodo = periodMatch[1].trim();
    } else {
        // Fallback: Try looking for a date range pattern anywhere "DD MMM YY - DD MMM YY"
        const rangeMatch = text.match(/(\d{1,2}\s+[a-zA-Z]{3}\s+\d{2})\s*-\s*(\d{1,2}\s+[a-zA-Z]{3}\s+\d{2})/);
        if (rangeMatch) {
            extracted.periodo = rangeMatch[0];
        }
    }

    return extracted;
};
